// ── Gantt view of the board page (TPT591) ──
//
// Third board sub-view beside kanban.js and sprint-board.js: one lane per sprint on a day-column
// time axis, a rail from the sprint's start_date to its end_date, and a bar per task from the
// sprint start (or its created_at) to its due date. The axis and lane rules live in
// lib/gantt-model.js (pure, unit-tested); this module renders that model and wires the task
// drawer and the hover/focus tooltip. It never fetches: the rows are board.js's (C1261).

import { escapeHtml } from '../utils.js';
import { openTaskDetail, closeTaskDetail } from '../components/task-detail.js';
import { statusLabel, closedNames } from '../lib/statuses.js';
import { dueDayDiff, shortDay, formatDay } from '../lib/due-tasks.js';
import { scheduleSummary } from '../lib/sprint-dates.js';
import { buildGanttModel, addDays } from '../lib/gantt-model.js';

const MIN_SCROLL_HEIGHT = 320; // px — below this the page scrolls instead
const FIT_GAP = 16; // px left under the chart when it is fitted to the window
const TIP_GAP = 8; // px between a mark and its tooltip, and the tooltip and the window edge

function freshState() {
  return {
    projectId: null,
    tasks: [], // board.js's rows — read-only here (C1261)
    statuses: [],
    sprintRows: null, // board.js's project-wide `{ priority }` rows for the drawer's Sprint select (TPT628)
    groupLabel: 'Sprint',
    groupLabelPlural: 'Sprints',
    canWrite: true,
    onDataChanged: null, // bubbles "server data changed" up to board.js (C1261) — see reloadTasks()
    model: null,
    rowsByKey: new Map(),
    lanesByPriority: new Map(),
    root: null,
    scroller: null,
    tip: null, // the body-level tooltip element, created on first use
    tipTarget: null,
    tipByFocus: false, // shown for keyboard focus rather than the pointer
    // Owner token of the drawer this view opened (TPT647), passed to closeTaskDetail() on teardown
    // so a late teardown leaves another page's drawer open.
    detailToken: null,
  };
}

let state = freshState();

function resetState() {
  state = freshState();
}

// Module-scoped, keyed by projectId — survives the teardown + re-render board.js runs on every
// filter change and (C1261) on every drawer close, like sprint-board.js's collapsedLanes. The
// left edge is kept as a day, not a pixel offset, because a filter change can move the axis start.
let scrollMemory = { projectId: null, leftDay: null, top: 0 };
// The task whose drawer was opened from the keyboard. The re-render after that drawer closes
// destroys the focused bar (focus falls to <body>), so the next render focuses its new one.
let focusMemory = { projectId: null, taskKey: null };

// Bubble a data-changing event (the drawer closing) up to board.js's own reload instead of
// refetching here. Read at call time: the drawer's onClose fires ~220ms after close, and after a
// teardown it must do nothing.
async function reloadTasks() {
  if (!state.onDataChanged) return;
  await state.onDataChanged();
}

// escapeHtml() leaves quotes alone (a browser's text serializer only escapes & < >), so text going
// into a double-quoted attribute needs this. Same helper as components/member-combo.js.
function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

const geometry = (from, to) => `--from:${from};--len:${to - from + 1}`;

// ── Markup ──

function renderLegend(model, ctx) {
  const key = (swatch, label) => `<span class="gantt-key"><span class="gantt-swatch gantt-swatch--${swatch}"></span>${escapeHtml(label)}</span>`;
  let html = '<div class="gantt-legend">';
  html += key('rail', ctx.groupLabel);
  html += key('bar', 'Task');
  html += key('open', 'No due date');
  html += key('overdue', 'Overdue');
  html += key('done', 'Done');
  if (model.axis.todayIndex != null) html += key('today', 'Today');
  if (model.axis.fallback) html += `<span class="gantt-legend-note">No ${escapeHtml(ctx.groupLabel.toLowerCase())} has dates yet, so the timeline shows the 30 days either side of today.</span>`;
  return html + '</div>';
}

function renderHeader(axis, ctx) {
  let weeks = '';
  for (const week of axis.weeks) weeks += `<div class="gantt-week">${escapeHtml(week.label)}</div>`;
  let days = '';
  for (let i = 0; i < axis.days; i++) {
    const classes = ['gantt-day'];
    if (i % 7 >= 5) classes.push('is-weekend'); // the axis starts on a Monday
    if (i === axis.todayIndex) classes.push('is-today');
    days += `<div class="${classes.join(' ')}">${Number(addDays(axis.start, i).slice(8))}</div>`;
  }
  return '<div class="gantt-head">'
    + `<div class="gantt-corner">${escapeHtml(ctx.groupLabelPlural)}</div>`
    + `<div class="gantt-head-track"><div class="gantt-weeks">${weeks}</div><div class="gantt-days">${days}</div></div>`
    + '</div>';
}

function renderCount(done, total) {
  return `<span class="gantt-count">${done}/${total} done</span>`;
}

function renderSprintLane(lane, ctx) {
  const { bar } = lane;
  let html = `<section class="gantt-lane${lane.closed ? ' is-closed' : ''}" data-priority="${lane.priority}">`;
  html += `<div class="gantt-window" style="${geometry(bar.from, bar.to)}" aria-hidden="true"></div>`;
  html += '<div class="gantt-row gantt-row--sprint"><div class="gantt-label">';
  html += `<span class="gantt-lane-headline"><span class="gantt-lane-title">${escapeHtml(lane.title)}</span>`;
  if (lane.closed) html += '<span class="badge badge-canceled">Closed</span>';
  html += '</span><span class="gantt-lane-meta">';
  if (lane.schedule) {
    html += `<span class="gantt-lane-range" title="${escapeAttr(lane.schedule.title)}">${escapeHtml(lane.schedule.range)}</span>`;
    // Same countdown pill as the sprint board's lane header, red for an open sprint past its end.
    if (!lane.closed && lane.schedule.label) html += `<span class="sprint-lane-days${lane.late ? ' overdue' : ''}">${escapeHtml(lane.schedule.label)}</span>`;
  }
  html += renderCount(lane.done, lane.total) + '</span></div>';
  const railClasses = ['gantt-rail'];
  if (bar.openStart) railClasses.push('is-open-start');
  if (bar.openEnd) railClasses.push('is-open-end');
  const railLabel = lane.schedule ? `${lane.title}: ${lane.schedule.title}` : lane.title;
  html += `<div class="gantt-track"><span class="${railClasses.join(' ')}" style="${geometry(bar.from, bar.to)}" role="img" aria-label="${escapeAttr(railLabel)}"></span></div>`;
  html += '</div>';
  html += lane.rows.map(row => renderTaskRow(row, ctx)).join('');
  return html + '</section>';
}

function renderUnscheduledLane(lane, ctx) {
  let html = '<section class="gantt-lane gantt-lane--unscheduled">';
  html += '<div class="gantt-row gantt-row--sprint"><div class="gantt-label">';
  html += `<span class="gantt-lane-headline"><span class="gantt-lane-title">${escapeHtml(lane.title)}</span></span>`;
  html += `<span class="gantt-lane-meta"><span class="gantt-lane-range">No dates</span>${renderCount(lane.done, lane.total)}</span>`;
  html += '</div><div class="gantt-track">';
  // Only a member who can set the dates (the board's Sprints manager needs task:write) gets the hint.
  if (ctx.canWrite && lane.groups.some(g => g.priority > 0)) {
    html += `<span class="gantt-track-note">Set ${escapeHtml(ctx.groupLabel.toLowerCase())} dates under ${escapeHtml(ctx.groupLabelPlural)} to place them on the timeline.</span>`;
  }
  html += '</div></div>';
  for (const group of lane.groups) {
    html += `<div class="gantt-row gantt-row--group" data-priority="${group.priority}"><div class="gantt-label">`;
    html += `<span class="gantt-group-title">${escapeHtml(group.title)}</span>`;
    if (group.closed) html += '<span class="badge badge-canceled">Closed</span>';
    html += renderCount(group.done, group.total) + '</div><div class="gantt-track"></div></div>';
    html += group.rows.map(row => renderTaskRow(row, ctx)).join('');
  }
  return html + '</section>';
}

// The row's state, readable without hovering: overdue, the due date, the closed status, or "No dates".
function renderDuePill(row) {
  if (row.done) return `<span class="gantt-due is-done">${escapeHtml(statusLabel(row.task.status))}</span>`;
  if (row.dueDate) {
    const text = row.overdue ? row.due.label : shortDay(row.dueDate);
    return `<span class="gantt-due${row.overdue ? ' overdue' : ''}" title="Due ${escapeAttr(formatDay(row.dueDate))}">${escapeHtml(text)}</span>`;
  }
  if (row.kind === 'none') return '<span class="gantt-due is-muted">No dates</span>';
  return '';
}

function renderTaskMark(row, ctx) {
  if (row.kind === 'none') return '';
  if (row.offAxis) return `<span class="gantt-off gantt-off--${row.offAxis}" aria-hidden="true">${row.offAxis === 'before' ? '‹' : '›'}</span>`;
  const classes = ['gantt-bar'];
  if (row.kind === 'point') classes.push('gantt-bar--point');
  else if (row.openEnd) classes.push('is-open');
  if (row.done) classes.push('is-done');
  else if (row.overdue) classes.push('is-overdue');
  if (row.clipStart) classes.push('is-clip-start');
  if (row.clipEnd) classes.push('is-clip-end');
  return `<button type="button" class="${classes.join(' ')}" style="${geometry(row.from, row.to)}" aria-label="${escapeAttr(taskTipLines(row, ctx.groupLabel).join('. '))}"></button>`;
}

function renderTaskRow(row, ctx) {
  const task = row.task;
  const key = String(task.task_key);
  const category = task.category === 'CODING' ? 'badge-coding' : 'badge-human';
  // `.task-card` + data-task-key: the drawer's board focus (TPT378) rings, lifts and keeps this row
  // sharp while it blurs the others — see lib/hover-expand.js focusBoardCard().
  let html = `<div class="gantt-row gantt-row--task task-card${row.done ? ' is-done' : ''}" data-task-key="${escapeAttr(key)}">`;
  html += '<div class="gantt-label">';
  html += `<a class="badge ${category} task-key-link" href="#/projects/${escapeAttr(String(ctx.projectId))}/tasks/${encodeURIComponent(key)}" draggable="false" title="Open task page">${escapeHtml(key)}</a>`;
  html += `<span class="gantt-task-title">${escapeHtml(task.title)}</span>`;
  html += renderDuePill(row);
  html += `</div><div class="gantt-track">${renderTaskMark(row, ctx)}</div></div>`;
  return html;
}

// The whole chart (legend + scroller) for `model`. `ctx` = { projectId, groupLabel,
// groupLabelPlural, canWrite }. Exported for gantt.test.js.
export function renderGanttMarkup(model, ctx) {
  const { axis, lanes } = model;
  let html = renderLegend(model, ctx);
  html += '<div class="gantt-scroll">';
  html += `<div class="gantt-canvas" style="--gantt-days:${axis.days}">`;
  html += renderHeader(axis, ctx);
  html += '<div class="gantt-body">';
  if (axis.todayIndex != null) html += `<div class="gantt-today" style="--at:${axis.todayIndex}" aria-hidden="true"></div>`;
  html += lanes.map(lane => (lane.kind === 'sprint' ? renderSprintLane(lane, ctx) : renderUnscheduledLane(lane, ctx))).join('');
  html += '</div></div></div>';
  return html;
}

// ── Tooltip ──
// Readout lines, the value first. Also the bar's aria-label, so focus and hover say the same.

function rangeText(row) {
  if (row.kind === 'point') return shortDay(row.start);
  if (row.openEnd) return `${shortDay(row.start)} – no due date`;
  const days = dueDayDiff(row.end, row.start) + 1;
  return `${shortDay(row.start)} – ${shortDay(row.end)} · ${days} ${days === 1 ? 'day' : 'days'}`;
}

// `noun` is the project's singular group label ("Sprint", "Batch", …).
function taskTipLines(row, noun) {
  const task = row.task;
  const lower = noun.toLowerCase();
  let status = statusLabel(task.status);
  if (row.done) status += row.dueDate ? '' : ` · last updated ${shortDay(row.end)}`;
  else if (row.due) status += ` · ${row.due.label}`;
  else if (row.kind === 'point') status += ` · started after its ${lower} ended`;
  else status += ` · runs to the end of its ${lower}`;
  return [rangeText(row), `${task.task_key} · ${task.title || ''}`, status];
}

function sprintTipLines(lane) {
  const schedule = lane.schedule;
  const summary = scheduleSummary(schedule);
  return [
    schedule ? schedule.range + (summary ? ` · ${summary}` : '') : lane.title,
    lane.title + (lane.closed ? ' · Closed' : ''),
    `${lane.done} of ${lane.total} done`,
  ];
}

function ensureTip() {
  if (!state.tip) {
    const tip = document.createElement('div');
    tip.className = 'gantt-tip';
    tip.setAttribute('role', 'tooltip');
    tip.hidden = true;
    document.body.appendChild(tip);
    state.tip = tip;
  }
  return state.tip;
}

function tipLinesFor(target) {
  if (target.classList.contains('gantt-rail')) {
    const lane = state.lanesByPriority.get(Number(target.closest('.gantt-lane')?.dataset.priority));
    return lane ? sprintTipLines(lane) : null;
  }
  const row = state.rowsByKey.get(target.closest('.gantt-row--task')?.dataset.taskKey);
  return row ? taskTipLines(row, state.groupLabel) : null;
}

function showTip(target, clientX, byFocus = false) {
  const lines = tipLinesFor(target);
  if (!lines) {
    hideTip();
    return;
  }
  const tip = ensureTip();
  // textContent only — titles and sprint names are user data.
  tip.replaceChildren(...lines.map((text, i) => {
    const line = document.createElement('div');
    line.className = i === 0 ? 'gantt-tip-value' : 'gantt-tip-line';
    line.textContent = text;
    return line;
  }));
  tip.hidden = false;
  state.tipTarget = target;
  state.tipByFocus = byFocus;
  placeTip(target, clientX);
}

// Over the pointer (or the visible middle of the mark), above it when there is room. A mark
// scrolled out of the chart takes its tooltip with it.
function placeTip(target, clientX) {
  const tip = state.tip;
  const rect = target.getBoundingClientRect();
  const box = state.scroller ? state.scroller.getBoundingClientRect() : rect;
  const left = Math.max(rect.left, box.left);
  const right = Math.min(rect.right, box.right);
  if (right <= left || rect.bottom <= box.top || rect.top >= box.bottom) {
    hideTip();
    return;
  }
  const x = clientX != null ? clientX : (left + right) / 2;
  const width = tip.offsetWidth;
  const height = tip.offsetHeight;
  tip.style.left = Math.round(Math.max(TIP_GAP, Math.min(x - width / 2, window.innerWidth - width - TIP_GAP))) + 'px';
  const above = rect.top - height - TIP_GAP;
  tip.style.top = Math.round(above >= box.top ? above : rect.bottom + TIP_GAP) + 'px';
}

function hideTip() {
  if (state.tip) state.tip.hidden = true;
  state.tipTarget = null;
  state.tipByFocus = false;
}

// Any scroll moves the marks under the fixed tooltip. One shown for the pointer goes away; one
// shown for keyboard focus follows its bar — focusing a bar off screen scrolls it into view.
function onAnyScroll() {
  const target = state.tipTarget;
  if (target && state.tipByFocus && target.isConnected && document.activeElement === target) placeTip(target);
  else hideTip();
}

const MARK_SELECTOR = '.gantt-bar, .gantt-rail';

function onPointerOver(e) {
  const mark = e.target.closest(MARK_SELECTOR);
  if (mark) showTip(mark, e.clientX);
}

function onPointerOut(e) {
  if (state.tipTarget && !(e.relatedTarget && state.tipTarget.contains(e.relatedTarget))) hideTip();
}

function onFocusIn(e) {
  const mark = e.target.closest(MARK_SELECTOR);
  if (mark) showTip(mark, null, true);
}

// ── Sizing and scroll position ──

function cssPx(name, fallback) {
  const value = state.root ? parseFloat(getComputedStyle(state.root).getPropertyValue(name)) : NaN;
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

// The scroller fills the window below its own top edge, so the sticky header and label column
// and both scrollbars stay on screen. Without this the CSS max-height (100dvh) still applies.
function fitHeight() {
  const scroller = state.scroller;
  if (!scroller) return;
  const top = Math.max(0, scroller.getBoundingClientRect().top);
  scroller.style.maxHeight = Math.max(MIN_SCROLL_HEIGHT, Math.floor(window.innerHeight - top - FIT_GAP)) + 'px';
}

function onResize() {
  hideTip();
  fitHeight();
}

function restoreScroll() {
  const { scroller, model } = state;
  if (!scroller) return;
  const dayWidth = cssPx('--gantt-day-w', 28);
  if (scrollMemory.projectId === state.projectId && scrollMemory.leftDay) {
    scroller.scrollLeft = Math.max(0, dueDayDiff(scrollMemory.leftDay, model.axis.start) * dayWidth);
    scroller.scrollTop = scrollMemory.top;
    return;
  }
  // First render for this project: today a quarter of the way in, or the end when every sprint is past.
  const { axis } = model;
  const visible = scroller.clientWidth - cssPx('--gantt-label-w', 300);
  if (axis.todayIndex != null) scroller.scrollLeft = Math.max(0, axis.todayIndex * dayWidth - visible / 4);
  else if (axis.today > axis.end) scroller.scrollLeft = scroller.scrollWidth;
  scrollMemory = { projectId: state.projectId, leftDay: null, top: 0 };
}

function onScroll() {
  const { scroller, model } = state;
  if (!scroller || !model) return;
  const dayWidth = cssPx('--gantt-day-w', 28);
  scrollMemory = {
    projectId: state.projectId,
    leftDay: addDays(model.axis.start, Math.round(scroller.scrollLeft / dayWidth)),
    top: scroller.scrollTop,
  };
}

function findRow(taskKey) {
  for (const row of state.root.querySelectorAll('.gantt-row--task')) {
    if (row.dataset.taskKey === taskKey) return row;
  }
  return null;
}

function restoreFocus() {
  if (focusMemory.projectId !== state.projectId || !focusMemory.taskKey) return;
  const taskKey = focusMemory.taskKey;
  focusMemory = { projectId: null, taskKey: null };
  if (document.activeElement && document.activeElement !== document.body) return; // focus moved on
  const row = findRow(taskKey);
  const target = row && (row.querySelector('.gantt-bar') || row.querySelector('.task-key-link'));
  if (target) target.focus({ preventScroll: true });
}

// The drawer lifts the focused row above its overlay (TPT378); a row half under the sticky header
// would then paint over it, so it is scrolled clear first.
function revealRow(row) {
  const scroller = state.scroller;
  const head = scroller.querySelector('.gantt-head');
  const inner = scroller.getBoundingClientRect().top + scroller.clientTop;
  const top = head ? head.getBoundingClientRect().bottom : inner;
  const bottom = inner + scroller.clientHeight;
  const rect = row.getBoundingClientRect();
  if (rect.top < top) scroller.scrollTop -= top - rect.top;
  else if (rect.bottom > bottom) scroller.scrollTop += rect.bottom - bottom;
}

// ── Task detail drawer ──

function onClick(e) {
  if (e.target.closest('a')) return; // the task-key permalink navigates (C1167)
  const row = e.target.closest('.gantt-row--task');
  if (!row) return;
  hideTip();
  revealRow(row);
  const taskKey = row.dataset.taskKey;
  // `detail` is 0 for a click synthesized from Enter/Space on the bar button.
  focusMemory = e.detail === 0 ? { projectId: state.projectId, taskKey } : { projectId: null, taskKey: null };
  state.detailToken = openTaskDetail(state.projectId, taskKey, {
    allTasks: state.tasks,
    sprintRows: state.sprintRows,
    focusCard: row,
    focusBoard: state.root,
    onClose: () => reloadTasks(),
  });
}

// ── Sub-view render (used by board container) ──

// `options` = { projectId, statuses, sprintRows, groupLabel, groupLabelPlural, onDataChanged,
// canWrite }. Returns the teardown board.js stores as state.subViewCleanup.
export function renderGanttView(container, tasks, sprints, options = {}) {
  resetState();
  state.projectId = options.projectId;
  state.tasks = tasks;
  state.statuses = options.statuses || [];
  state.sprintRows = options.sprintRows || null;
  state.groupLabel = options.groupLabel || 'Sprint';
  state.groupLabelPlural = options.groupLabelPlural || 'Sprints';
  state.canWrite = options.canWrite !== false;
  state.onDataChanged = options.onDataChanged || null;
  state.model = buildGanttModel({ tasks, sprints: sprints || [], closed: closedNames(state.statuses), groupLabel: state.groupLabel });

  for (const lane of state.model.lanes) {
    if (lane.kind === 'sprint') state.lanesByPriority.set(lane.priority, lane);
    for (const group of lane.kind === 'sprint' ? [lane] : lane.groups) {
      for (const row of group.rows) state.rowsByKey.set(String(row.task.task_key), row);
    }
  }

  const ctx = { projectId: state.projectId, groupLabel: state.groupLabel, groupLabelPlural: state.groupLabelPlural, canWrite: state.canWrite };
  container.innerHTML = `<div id="gantt-board" class="gantt">${renderGanttMarkup(state.model, ctx)}</div>`;
  const root = document.getElementById('gantt-board');
  state.root = root;
  state.scroller = root.querySelector('.gantt-scroll');

  fitHeight();
  restoreScroll();
  restoreFocus();

  // Listeners on the root die with it on the next render; the window ones are removed below.
  root.addEventListener('click', onClick);
  root.addEventListener('pointerover', onPointerOver);
  root.addEventListener('pointerout', onPointerOut);
  root.addEventListener('pointerdown', hideTip);
  root.addEventListener('focusin', onFocusIn);
  root.addEventListener('focusout', hideTip);
  state.scroller.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  // Capture: scroll events don't bubble, and this hears the chart's own scroller as well as the page.
  window.addEventListener('scroll', onAnyScroll, { capture: true, passive: true });

  return () => {
    // Only what this view opened (TPT647): its drawer. Modals are board.js's, and the drawer owns
    // its own confirms.
    closeTaskDetail(state.detailToken);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onAnyScroll, { capture: true });
    if (state.tip) state.tip.remove();
    resetState();
  };
}
